// src/pages/admin/AdminSuscripciones.js
//
// D-24 — Suscripciones y cobro mensual de todos los negocios.
//
//   · Resumen del período: facturado, pagado, pendiente, vencido, recaudado.
//   · Negocio por negocio: plan, estado de cobro (al día, pendiente, en mora),
//     ciclo pagado hasta, próximo corte, cuenta de cobro del mes, días de mora.
//   · Acciones: ver PDF, reenviar por correo, registrar pago manual, extender
//     plazo, ejecutar el ciclo de cobro ahora y exportar a CSV.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField, Button, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton, Tooltip, Alert,
  LinearProgress, Grid,
} from '@mui/material';
import AutorenewIcon      from '@mui/icons-material/Autorenew';
import PlayArrowIcon      from '@mui/icons-material/PlayArrow';
import PictureAsPdfIcon   from '@mui/icons-material/PictureAsPdf';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import EventRepeatIcon    from '@mui/icons-material/EventRepeat';
import CheckIcon          from '@mui/icons-material/Check';
import DownloadIcon       from '@mui/icons-material/Download';
import RefreshIcon        from '@mui/icons-material/Refresh';
import { suscripciones as apiSus, facturas as apiFac } from '../../api/tesoreriaApi';
import { useApp } from '../../context/AppContext';
import { DialogPago } from './AdminFacturasSaaS';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

const fechaCo = (iso, conHora = false) => {
  if (!iso) return '—';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('es-CO', {
    timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric',
    ...(conHora ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const ESTADOS_COBRO = [
  { value: '',                label: 'Todos' },
  { value: 'EN_MORA',         label: 'En mora (bloqueados)' },
  { value: 'PENDIENTE_PAGO',  label: 'Pendiente de pago' },
  { value: 'AL_DIA',          label: 'Al día' },
  { value: 'SIN_PRIMER_PAGO', label: 'Sin primer pago' },
];

const chipCobro = (estado, diasMora) => {
  switch (estado) {
    case 'EN_MORA':         return <Chip size="small" color="error" label={`En mora${diasMora ? ` · ${diasMora} d` : ''}`} />;
    case 'PENDIENTE_PAGO':  return <Chip size="small" color="warning" label="Pendiente" />;
    case 'AL_DIA':          return <Chip size="small" color="success" label="Al día" />;
    case 'SIN_PRIMER_PAGO': return <Chip size="small" variant="outlined" label="Sin primer pago" />;
    default:                return <Chip size="small" variant="outlined" label={estado} />;
  }
};

const nombrePlan = { 'plan-negocio': 'Negocio', 'plan-local': 'Local', 'plan-360': 'HolaVeci 360°' };

export default function AdminSuscripciones() {
  const { notificar } = useApp();
  const [estado, setEstado]     = useState('');
  const [texto, setTexto]       = useState('');
  const [datos, setDatos]       = useState(null);
  const [resumen, setResumen]   = useState(null);
  const [cargando, setCargando] = useState(false);
  const [ejecutando, setEjec]   = useState(false);
  const [dialogPago, setDialogPago] = useState(null);
  const [error, setError]       = useState('');

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const [l, r] = await Promise.all([
        apiSus.listar(estado ? { estado } : {}),
        apiSus.resumen(),
      ]);
      setDatos(l);
      setResumen(r);
    } catch (e) {
      // El error se queda en pantalla: antes solo salía un aviso que se
      // desvanecía y la tabla decía "no hay negocios con suscripción", que es
      // justo lo contrario de lo que pasa cuando la consulta falla.
      const detalle = e.mensajeUsuario || e.message || 'Error desconocido.';
      setError(`No se pudieron cargar las suscripciones: ${detalle}`);
      setDatos(null);
      setResumen(null);
      notificar(detalle, 'error');
    } finally { setCargando(false); }
  }, [estado, notificar]);

  useEffect(() => { cargar(); }, [cargar]);

  const items = useMemo(() => {
    const lista = datos?.items || [];
    const t = texto.trim().toLowerCase();
    if (!t) return lista;
    return lista.filter(i =>
      (i.negocio || '').toLowerCase().includes(t)
      || (i.razonSocial || '').toLowerCase().includes(t)
      || (i.telefono || '').includes(t)
      || (i.correo || '').toLowerCase().includes(t));
  }, [datos, texto]);

  const conteo = useMemo(() => {
    const c = { EN_MORA: 0, PENDIENTE_PAGO: 0, AL_DIA: 0 };
    (datos?.items || []).forEach(i => { if (c[i.estadoCobro] !== undefined) c[i.estadoCobro]++; });
    return c;
  }, [datos]);

  const ejecutarCiclo = async () => {
    if (!window.confirm('Ejecutar ahora el ciclo de cobro (mensualidades del mes, recordatorios y suspensión por mora)?')) return;
    setEjec(true);
    try { const r = await apiSus.procesarCiclo(); notificar(r.mensaje, 'success'); cargar(); }
    catch (e) { notificar(e.mensajeUsuario || 'No se pudo ejecutar el ciclo.', 'error'); }
    finally { setEjec(false); }
  };

  const facturaDe = (i) => i.facturaPendiente || i.facturaMes;

  const reenviar = async (i) => {
    const f = facturaDe(i); if (!f) return;
    try { const r = await apiFac.reenviar(f.facturaId); notificar(r.mensaje, 'success'); }
    catch (e) { notificar(e.mensajeUsuario || 'No se pudo enviar.', 'error'); }
  };

  const extender = async (i) => {
    const f = i.facturaPendiente; if (!f) return;
    const dias = Number(window.prompt(`Días adicionales para que ${i.negocio} pague ${f.numeroFactura} (1 a 30):`, '2'));
    if (!dias) return;
    try { const r = await apiFac.extenderPlazo(f.facturaId, dias); notificar(r.mensaje, 'success'); cargar(); }
    catch (e) { notificar(e.mensajeUsuario || 'No se pudo extender el plazo.', 'error'); }
  };

  const exportarCsv = () => {
    const encabezado = ['Negocio', 'Razón social', 'Teléfono', 'Correo', 'Plan', 'Mensualidad', 'Estado cobro',
      'Días mora', 'Pagado hasta', 'Próximo corte', 'Cuenta de cobro', 'Valor', 'Estado cuenta', 'Límite pago', 'Guía'];
    const filas = items.map(i => {
      const f = facturaDe(i);
      return [i.negocio, i.razonSocial, i.telefono, i.correo, nombrePlan[i.planCodigo] || i.planCodigo,
        i.precioMensual, i.estadoCobro, i.diasMora, fechaCo(i.fechaFinCicloActual), fechaCo(i.fechaProximoCobro),
        f?.numeroFactura || '', f?.total ?? '', f?.estado || '', f?.fechaLimitePago ? fechaCo(f.fechaLimitePago, true) : '',
        i.guia || ''];
    });
    const csv = [encabezado, ...filas]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `suscripciones-${datos?.periodo?.anio}-${String(datos?.periodo?.mes).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tarjeta = (titulo, valor, color, sub) => (
    <Card sx={{ height: '100%' }}><CardContent>
      <Typography variant="caption" color="text.secondary">{titulo}</Typography>
      <Typography variant="h5" color={color}>{valor}</Typography>
      {sub && <Typography variant="caption">{sub}</Typography>}
    </CardContent></Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={2} mb={3}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <AutorenewIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h4" fontFamily="Fraunces">Suscripciones</Typography>
            <Typography variant="body2" color="text.secondary">
              Corte el día 1 de cada mes · próximo corte: <b>{datos ? fechaCo(datos.proximoCorte) : '—'}</b>
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={cargar} disabled={cargando}>Actualizar</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportarCsv} disabled={!items.length}>
            Exportar CSV
          </Button>
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={ejecutarCiclo} disabled={ejecutando}>
            {ejecutando ? 'Ejecutando…' : 'Ejecutar ciclo de cobro'}
          </Button>
        </Stack>
      </Stack>

      {resumen && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2.4}>{tarjeta(`Facturado ${String(resumen.periodo.mes).padStart(2, '0')}/${resumen.periodo.anio}`,
            fmtCOP(resumen.facturado), 'text.primary', `${resumen.cantidad.total} cuentas · IVA ${fmtCOP(resumen.iva)}`)}</Grid>
          <Grid item xs={6} md={2.4}>{tarjeta('Pagado', fmtCOP(resumen.pagado), 'success.main', `${resumen.cantidad.pagadas} cuentas`)}</Grid>
          <Grid item xs={6} md={2.4}>{tarjeta('Pendiente', fmtCOP(resumen.pendiente), 'warning.main', `${resumen.cantidad.pendientes} cuentas`)}</Grid>
          <Grid item xs={6} md={2.4}>{tarjeta('Vencido', fmtCOP(resumen.vencido), 'error.main', `${resumen.cantidad.vencidas} cuentas`)}</Grid>
          <Grid item xs={12} md={2.4}>{tarjeta('Recaudado en el mes', fmtCOP(resumen.recaudadoEnMes), 'primary.main',
            `Planes ${fmtCOP(resumen.facturadoPlanes)} · ${resumen.pedidosCobrados || 0} pedidos ${fmtCOP(resumen.facturadoPedidos)}`)}</Grid>
        </Grid>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField select size="small" label="Estado de cobro" value={estado} sx={{ minWidth: 220 }}
              onChange={e => setEstado(e.target.value)}>
              {ESTADOS_COBRO.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Buscar negocio, teléfono o correo" value={texto}
              onChange={e => setTexto(e.target.value)} sx={{ minWidth: 280 }} />
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" spacing={1}>
              <Chip color="error" variant="outlined" label={`En mora: ${conteo.EN_MORA}`} onClick={() => setEstado('EN_MORA')} />
              <Chip color="warning" variant="outlined" label={`Pendientes: ${conteo.PENDIENTE_PAGO}`} onClick={() => setEstado('PENDIENTE_PAGO')} />
              <Chip color="success" variant="outlined" label={`Al día: ${conteo.AL_DIA}`} onClick={() => setEstado('AL_DIA')} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {cargando && <LinearProgress sx={{ mb: 1 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={cargar}>Reintentar</Button>
        }>
          {error}
        </Alert>
      )}
      {!cargando && !error && items.length === 0 ? (
        <Alert severity="info">No hay negocios con suscripción para estos filtros.</Alert>
      ) : error ? null : (
        <Card sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Negocio</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Cobro</TableCell>
                <TableCell>Pagado hasta</TableCell>
                <TableCell>Próximo corte</TableCell>
                <TableCell>Cuenta de cobro</TableCell>
                <TableCell>Límite de pago</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(i => {
                const f = facturaDe(i);
                const pendiente = i.facturaPendiente;
                return (
                  <TableRow key={i.tenantID ?? i.tenantId} hover>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography fontWeight={700}>{i.negocio}</Typography>
                      {i.razonSocial && i.razonSocial !== i.negocio && (
                        <Typography variant="caption" color="text.secondary" display="block">{i.razonSocial}</Typography>
                      )}
                      {i.guia && <Typography variant="caption" color="text.secondary" display="block">Guía: {i.guia}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={nombrePlan[i.planCodigo] || i.planCodigo} />
                      <Typography variant="caption" display="block">{fmtCOP(i.precioMensual)}/mes</Typography>
                    </TableCell>
                    <TableCell>{chipCobro(i.estadoCobro, i.diasMora)}</TableCell>
                    <TableCell>{fechaCo(i.fechaFinCicloActual)}</TableCell>
                    <TableCell>{fechaCo(i.fechaProximoCobro)}</TableCell>
                    <TableCell>
                      {f ? (
                        <>
                          <Typography variant="body2">{f.numeroFactura}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fmtCOP(f.total)} · {f.estado}
                          </Typography>
                          {f.pedidosCantidad > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Plan {fmtCOP(f.valorPlan)} + {f.pedidosCantidad} pedidos
                            </Typography>
                          )}
                        </>
                      ) : <span style={{ color: '#999' }}>—</span>}
                    </TableCell>
                    <TableCell>{pendiente?.fechaLimitePago ? fechaCo(pendiente.fechaLimitePago, true) : '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{i.telefono || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{i.correo || ''}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {f && (
                        <>
                          <Tooltip title="Ver PDF">
                            <IconButton size="small" onClick={() => apiFac.abrirPdf(f.facturaId).catch(e => notificar(e.message, 'error'))}>
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Enviar por correo">
                            <IconButton size="small" onClick={() => reenviar(i)}><ForwardToInboxIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {pendiente && (
                        <>
                          <Tooltip title="Extender plazo">
                            <IconButton size="small" color="info" onClick={() => extender(i)}><EventRepeatIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Registrar pago manual">
                            <IconButton size="small" color="success"
                              onClick={() => setDialogPago({ ...pendiente, nombreComercial: i.negocio })}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <DialogPago factura={dialogPago} onCerrar={() => setDialogPago(null)}
                  onPagada={() => { setDialogPago(null); cargar(); }} />
    </Container>
  );
}
